import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

const STORAGE_KEY = "reduce_motion";

const getStoredPreference = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === null) {
    return null;
  }

  return stored === "true";
};

const getSystemPreference = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

type ReducedMotionContextValue = {
  reducedMotion: boolean;
  setReducedMotion: (value: boolean) => void;
};

const ReducedMotionContext = createContext<ReducedMotionContextValue | undefined>(undefined);

export const ReducedMotionProvider = ({ children }: { children: ReactNode }) => {
  const storedPreference = getStoredPreference();
  const [hasStoredPreference, setHasStoredPreference] = useState(storedPreference !== null);
  const [reducedMotion, setReducedMotionState] = useState(
    storedPreference ?? getSystemPreference()
  );

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("reduce-motion", reducedMotion);
  }, [reducedMotion]);

  useEffect(() => {
    if (hasStoredPreference || typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = () => setReducedMotionState(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [hasStoredPreference]);

  const setReducedMotion = (value: boolean) => {
    setHasStoredPreference(true);
    setReducedMotionState(value);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, String(value));
    }
  };

  const contextValue = useMemo(
    () => ({
      reducedMotion,
      setReducedMotion,
    }),
    [reducedMotion]
  );

  return (
    <ReducedMotionContext.Provider value={contextValue}>
      {children}
    </ReducedMotionContext.Provider>
  );
};

export const useReducedMotionSetting = () => {
  const context = useContext(ReducedMotionContext);
  if (!context) {
    throw new Error("useReducedMotionSetting must be used within ReducedMotionProvider");
  }

  return context;
};
